import {
  Notification,
  NotificationFCMData,
  PUSH_CHANNELS,
} from "@config/push-notifications";
import { phoneToIsraelSignedPhone } from "@shared/helpers/phone-to-israel-signed-phone";
import axios from "axios";
import twilio from "twilio";
import admin from "../firebase";
import { transporter } from "../nodemailer";

const SMS_LEGACY_TIMEOUT_MS = 25000;
const SMS_TWILIO_TIMEOUT_MS = 12000;

export type SmsRecipientResult = {
  recipient: string;
  success: boolean;
  /** True when the provider may have accepted the message but we got no confirmation (e.g. timeout). */
  deliveryUncertain?: boolean;
};

function isSmsDeliveryUncertainError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return (
      error.code === "ECONNABORTED" ||
      error.code === "ETIMEDOUT" ||
      error.message.toLowerCase().includes("timeout")
    );
  }
  if (error instanceof Error) {
    return error.message.toLowerCase().includes("timed out");
  }
  return false;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  if (!timeoutMs || timeoutMs <= 0) return promise;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer);
  }) as Promise<T>;
}

/**
 * 📱 Serializes a notification into FCM data payload (string values).
 * @param notification - Notification to serialize
 * @returns Flattened key-value object for FCM data
 */
export function stringifyNotification(notification: Notification) {
  const obj: NotificationFCMData = {
    id: notification.id ? notification.id.toString() : undefined,
    title: notification.title,
    body: notification.body,
    channelId: notification.channelId,
    ongoing: notification.ongoing ? notification.ongoing.toString() : undefined,
    actions: notification.actions
      ? JSON.stringify({
          ...notification.actions,
          extra: Object.entries(notification.actions.extra).reduce(
            (prev, [key, value]) => ({
              ...prev,
              [key]: JSON.stringify(value),
            }),
            {},
          ),
        })
      : undefined,
    mainAction: notification.mainAction
      ? JSON.stringify({
          id: notification.mainAction.id,
          extra: JSON.stringify(notification.mainAction.extra),
        })
      : undefined,
    group: notification.group,
  };

  return Object.entries(obj).reduce(
    (prev, [key, value]) =>
      value === undefined ? prev : { ...prev, [key]: value },
    {} as Required<NotificationFCMData>,
  );
}

/**
 * 📲 Sends a message via Whatsapp.
 * @param config - message, recipients, optional senderName, includedUrl, sendInDev
 * @returns Per-recipient success status
 */
export async function sendWhatsapp(config: {
  message: string;
  recipients: string[];
  senderName?: string;
  includedUrl?: string;
  sendInDev?: boolean;
}): Promise<
  {
    recipient: string;
    success: boolean;
  }[]
> {
  const {
    message,
    recipients,
    senderName = "מערכת Weme",
    includedUrl,
    sendInDev = false,
  } = config;

  if (!sendInDev) {
    console.log(
      "[Whatsapp] Not sent in dev. Content:",
      JSON.stringify({ message, recipients, senderName, includedUrl }),
    );
    return recipients.map((recipient) => ({ recipient, success: true }));
  }

  const template = `he_general${includedUrl ? "_btn" : ""}_1`; // if there is the `includedUrl` then use the btn template
  const PHONE_NUMBER_ID = process.env["PHONE_NUMBER_ID"] || "429014373632339";
  const ACCESS_TOKEN = process.env["WHATSAPP_ACCESS_TOKEN"] || "";

  const whatsappBody: Record<string, any> = {
    messaging_product: "whatsapp",
    type: "template",
    template: {
      name: template,
      language: {
        code: "he",
      },
      components: [
        {
          type: "body",
          parameters: [
            {
              type: "text",
              text: senderName,
            },
            {
              type: "text",
              text: message,
            },
          ],
        },
      ],
    },
  };

  // Add button if `goToWebUrlParam` is provided
  if (includedUrl) {
    whatsappBody.template.components.push({
      type: "button",
      sub_type: "url",
      index: 0,
      parameters: [
        {
          type: "text",
          text: includedUrl,
        },
      ],
    });
  }

  const results = await Promise.allSettled(
    recipients.map(async (phone) => {
      const response = await axios.post(
        `https://graph.facebook.com/v16.0/${PHONE_NUMBER_ID}/messages`,
        {
          ...whatsappBody,
          to: phoneToIsraelSignedPhone(phone).slice(1), // remove the + sign at the start
        },
        {
          headers: {
            Authorization: `Bearer ${ACCESS_TOKEN}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (response.data?.messages?.[0]?.message_status !== "accepted") {
        console.error(
          "Whatsapp message not accepted for phone ",
          `'${phone}'`,
          response.data,
        );
        throw {
          message: "response message is not accepted",
        };
      }
    }),
  );

  return results.map((v, i) => ({
    recipient: recipients[i],
    success: v.status === "fulfilled",
  }));
}

/**
 * 📧 Sends a message via Email.
 * @param config - message, recipients, optional sendInDev
 * @returns Per-recipient success status
 */
export async function sendEmail(config: {
  message: string;
  recipients: string[];
  sendInDev?: boolean;
}): Promise<
  {
    recipient: string;
    success: boolean;
  }[]
> {
  const { message, recipients, sendInDev = false } = config;

  if (!sendInDev) {
    console.log(
      "[Email] Not sent in dev. Content:",
      JSON.stringify({ message, recipients }),
    );
    return recipients.map((recipient) => ({ recipient, success: true }));
  }

  // if there is no transporter than we cannot email anyone
  if (!transporter) {
    console.error("No transporter found - cannot send email");
    return recipients.map((v) => ({
      recipient: v,
      success: false,
    }));
  }

  const wemeUser = process.env.GMAIL_USER;

  const results = await Promise.allSettled(
    recipients.map((recipient) => {
      transporter.sendMail({
        from: `Weme <${wemeUser}>`,
        to: recipient,
        subject: "מערכת Weme | הודעה חדשה",
        text: message,
      });
    }),
  );

  return results.map((r, i) => ({
    recipient: recipients[i],
    method: "email",
    success: r.status === "fulfilled",
  }));
}

/**
 * 📱 Sends SMS to recipients.
 * @param config - message, recipients, optional isOtp, sendInDev
 * @returns Per-recipient success status
 */
export async function sendSms(config: {
  message: string;
  recipients: string[];
  isOtp?: boolean;
  sendInDev?: boolean;
}): Promise<SmsRecipientResult[]> {
  const { recipients, message, isOtp, sendInDev = false } = config;

  // ב-production תמיד שולחים. ב-development שולחים רק אם sendInDev=true (למשל לבדיקות)
  const shouldSkip = process.env.MODE === "development" && !sendInDev;
  if (shouldSkip) {
    console.log(
      "[SMS] Not sent in dev. Content:",
      JSON.stringify({ message, recipients, isOtp }),
    );
    return recipients.map((recipient) => ({ recipient, success: true }));
  }

  const providers = getSmsProvidersChain();
  return await sendSmsWithFallback({ recipients, message, isOtp }, providers);
}

type SmsProvider = "legacy" | "twilio";

function getSmsProvidersChain(): SmsProvider[] {
  // Preferred: explicit chain, e.g. "legacy,twilio"
  const chainRaw = (process.env["SMS_PROVIDERS"] ?? "").trim();
  if (chainRaw) {
    const parsed = chainRaw
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
      .map((p) => (p === "twilio" ? "twilio" : p === "legacy" ? "legacy" : null))
      .filter((p): p is SmsProvider => p !== null);
    const deduped = parsed.length ? [...new Set(parsed)] : (["legacy"] as SmsProvider[]);

    // Ensure legacy is the primary (default) provider when it's part of the chain.
    // This keeps existing behavior where legacy is the default, while still allowing
    // explicit single-provider selection via SMS_PROVIDER=twilio.
    if (deduped.includes("legacy")) {
      return ["legacy", ...deduped.filter((p) => p !== "legacy")];
    }
    return deduped;
  }

  // Backwards compatible: single provider selection
  const single = (process.env["SMS_PROVIDER"] ?? "legacy").trim().toLowerCase();
  return [single === "twilio" ? "twilio" : "legacy"];
}

async function sendSmsWithFallback(
  config: { recipients: string[]; message: string; isOtp?: boolean; },
  providers: SmsProvider[],
): Promise<SmsRecipientResult[]> {
  const { recipients, message, isOtp } = config;
  const chain = providers.length ? providers : (["legacy"] as SmsProvider[]);

  const results: SmsRecipientResult[] = recipients.map((r) => ({
    recipient: r,
    success: false,
  }));
  let pending = recipients.map((recipient, idx) => ({ recipient, idx }));

  for (const provider of chain) {
    if (pending.length === 0) break;

    const pendingRecipients = pending.map((p) => p.recipient);
    const attempt =
      provider === "twilio"
        ? await sendSmsTwilioBulk({ recipients: pendingRecipients, message })
        : await sendSmsLegacySoap({ recipients: pendingRecipients, message, isOtp });

    // Map provider results back to pending array by index
    const succeededIdx = new Set<number>();
    attempt.forEach((r, i) => {
      if (r.success) succeededIdx.add(i);
    });

    for (let i = 0; i < pending.length; i++) {
      if (succeededIdx.has(i)) {
        const p = pending[i]!;
        results[p.idx] = { recipient: p.recipient, success: true };
      } else {
        const attemptResult = attempt[i];
        if (attemptResult?.deliveryUncertain) {
          const p = pending[i]!;
          results[p.idx] = {
            recipient: p.recipient,
            success: false,
            deliveryUncertain: true,
          };
        }
      }
    }

    pending = pending.filter((_, i) => !succeededIdx.has(i));
  }

  return results;
}

export async function sendSmsTwilio(
  to: string,
  message: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await sendSmsTwilioBulk({ recipients: [to], message });
    const ok = res[0]?.success === true;
    return ok ? { success: true } : { success: false, error: "Failed to send SMS via Twilio" };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e.message : "Unknown error",
    };
  }
}

async function sendSmsTwilioBulk(config: {
  message: string;
  recipients: string[];
}): Promise<SmsRecipientResult[]> {
  const accountSid = process.env["TWILIO_ACCOUNT_SID"] ?? "";
  const authToken = process.env["TWILIO_AUTH_TOKEN"] ?? "";
  const from = process.env["TWILIO_FROM"] ?? "";
  const messagingServiceSid = process.env["TWILIO_MESSAGING_SERVICE_SID"] ?? "";

  if (!accountSid || !authToken) {
    console.error("[SMS:Twilio] Missing TWILIO_ACCOUNT_SID/TWILIO_AUTH_TOKEN");
    return config.recipients.map((recipient) => ({ recipient, success: false }));
  }
  if (!from && !messagingServiceSid) {
    console.error("[SMS:Twilio] Missing TWILIO_FROM or TWILIO_MESSAGING_SERVICE_SID");
    return config.recipients.map((recipient) => ({ recipient, success: false }));
  }

  const client = twilio(accountSid, authToken);
  const results = await Promise.allSettled(
    config.recipients.map(async (recipient) => {
      const to = phoneToIsraelSignedPhone(recipient);
      await withTimeout(
        client.messages.create({
          to,
          body: config.message,
          ...(messagingServiceSid ? { messagingServiceSid } : { from }),
        }),
        SMS_TWILIO_TIMEOUT_MS,
        "Twilio SMS send",
      );
      return true;
    }),
  );

  return results.map((r, i) => {
    const recipient = config.recipients[i]!;
    if (r.status === "rejected") {
      const reason =
        r.reason instanceof Error
          ? r.reason.message
          : typeof r.reason === "string"
            ? r.reason
            : JSON.stringify(r.reason);
      const deliveryUncertain = isSmsDeliveryUncertainError(r.reason);
      console.error("[SMS:Twilio] Send failed:", { recipient, reason, deliveryUncertain });
      return { recipient, success: false, deliveryUncertain };
    }
    return { recipient, success: true };
  });
}

async function sendSmsLegacySoap(config: {
  message: string;
  recipients: string[];
  isOtp?: boolean;
}): Promise<SmsRecipientResult[]> {
  function xmlEscape(value: string) {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
  }

  const messageKey = process.env.MESSAGE_KEY;
  if (!messageKey) {
    console.error("[SMS:Legacy] Missing MESSAGE_KEY");
    return config.recipients.map((recipient) => ({ recipient, success: false }));
  }

  const results = await Promise.allSettled(
    config.recipients.map(async (phoneNumber) => {
      const escapedDestination = xmlEscape(phoneToIsraelSignedPhone(phoneNumber));
      const cdataMessage = `<![CDATA[${config.message}]]>`;
      const data =
        '<?xml version="1.0" encoding="utf-8"?>' +
        '<soap12:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap12="http://www.w3.org/2003/05/soap-envelope">' +
        "<soap12:Body>" +
        '<sendSmsToRecipients xmlns="apiGlobalSms">' +
        "<ApiKey>" +
        messageKey +
        "</ApiKey>" +
        `<txtOriginator>${config.isOtp ? "Lev1" : "Lev Echad"}</txtOriginator>` +
        "<destinations>" +
        escapedDestination +
        "</destinations>" +
        "<txtSMSmessage>" +
        cdataMessage +
        "</txtSMSmessage>" +
        "<dteToDeliver></dteToDeliver>" +
        "<txtAddInf>jsnodetest</txtAddInf>" +
        "</sendSmsToRecipients>" +
        "</soap12:Body>" +
        "</soap12:Envelope>";

      const headers = {
        "Content-Type": "text/xml; charset=utf-8",
        SOAPAction: "apiGlobalSms/sendSmsToRecipients",
      };

      try {
        console.log("Sending SMS to:", phoneNumber);
        console.log("SMS message:", config.message);
        console.log("SMS originator:", config.isOtp ? "Lev1" : "LevEchad");

        const response = await axios.post(
          "https://sapi.itnewsletter.co.il//webservices/WsSMS.asmx",
          data,
          { headers, timeout: SMS_LEGACY_TIMEOUT_MS },
        );

        let res = <string>response.data.toString();
        const t = "<sendSmsToRecipientsResult>";
        const i = res.indexOf(t);
        if (i >= 0) {
          res = res.substring(i + t.length);
          res = res.substring(0, res.indexOf("<"));
        }
        const resultCode = (res ?? "").trim();
        const success = resultCode.length === 0 || resultCode === "0";
        if (!success) {
          console.error("[SMS:Legacy] Provider returned failure code:", resultCode);
        }
        return { success, deliveryUncertain: false };
      } catch (error) {
        const deliveryUncertain = isSmsDeliveryUncertainError(error);
        console.error("Error SMS:", error, { deliveryUncertain });
        if (error instanceof Error) {
          console.error("Error message:", error.message);
          console.error("Error stack:", error.stack);
        }
        if (axios.isAxiosError(error)) {
          console.error("Axios error response:", error.response?.data);
          console.error("Axios error status:", error.response?.status);
        }
        return { success: false, deliveryUncertain };
      }
    }),
  );

  return results.map((p, i) => ({
    recipient: config.recipients[i]!,
    success: p.status === "fulfilled" && !!p.value.success,
    deliveryUncertain:
      p.status === "fulfilled" ? p.value.deliveryUncertain : isSmsDeliveryUncertainError(p.reason),
  }));
}

/**
 * 🔔 Sends push notification to FCM tokens.
 * @param config - recipients (FCM tokens), notification payload, optional sendInDev
 * @returns Per-recipient success status
 */
export async function sendNotification(config: {
  recipients: string[];
  notification: Notification;
  sendInDev?: boolean;
}) {
  const { recipients, notification, sendInDev = false } = config;

  if (!sendInDev) {
    console.log(
      "[Push notification] Not sent in dev. Content:",
      JSON.stringify({
        recipients: recipients.length,
        notification: {
          title: notification.title,
          body: notification.body,
          channelId: notification.channelId,
        },
      }),
    );
    return recipients.map((recipient) => ({ recipient, success: true }));
  }

  let responses: admin.messaging.SendResponse[] = [];

  const sendResult = await admin.messaging().sendEachForMulticast({
    tokens: recipients,
    data: stringifyNotification(notification),
    android: { priority: "high" },
    apns: {
      payload: {
        aps: {
          alert: {
            title: notification.title,
            body: notification.body,
          },
          category: notification.actions?.category,
          sound: PUSH_CHANNELS.find((v) => v.id === notification.channelId)
            ?.sound,
          threadId: notification.group,
          contentAvailable: true,
        },
      },
    },
  });
  responses = sendResult.responses;
  return responses.map((response, i) => ({
    recipient: recipients[i],
    success: response.success,
  }));
}

const FCM_BATCH_SIZE = 500;

/**
 * Sends push notification to a single batch of FCM tokens (max 500).
 * Used by NotificationsService for chunked sending.
 */
export async function sendNotificationSingleBatch(
  tokens: string[],
  notification: Notification,
  sendInDev = false,
  extraData?: Record<string, string>,
): Promise<{ recipient: string; success: boolean; }[]> {
  if (tokens.length > FCM_BATCH_SIZE) {
    throw new Error(
      `sendNotificationSingleBatch accepts at most ${FCM_BATCH_SIZE} tokens, got ${tokens.length}`,
    );
  }
  if (!sendInDev && process.env.MODE === "development") {
    return tokens.map((recipient) => ({ recipient, success: true }));
  }
  if (tokens.length === 0) {
    return [];
  }
  const sendResult = await admin.messaging().sendEachForMulticast({
    tokens,
    data: {
      ...stringifyNotification(notification),
      ...(extraData ?? {}),
    },
    android: { priority: "high" },
    apns: {
      payload: {
        aps: {
          alert: {
            title: notification.title,
            body: notification.body,
          },
          category: notification.actions?.category,
          sound: PUSH_CHANNELS.find((v) => v.id === notification.channelId)
            ?.sound,
          threadId: notification.group,
          contentAvailable: true,
        },
      },
    },
  });
  return sendResult.responses.map((response, i) => ({
    recipient: tokens[i],
    success: response.success,
  }));
}
